import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setPageIndex, setRowsCount } from '../../redux/slices/paginationSlice';
import { Pagination } from '../../modules/clientModule/components/Common/Table/types';
import { useGetClientProjectsQuery, useGetProjectsQuery, useGetVocabularyQuery } from '../../redux/rtkQuery/apiSlice';
import PaginationMobile from '../Responsive/PaginationMobile';
import { RootState } from '../../redux/root';
import { Loader } from '../../atoms';
import newProject from '../../resources/icons/new-project.svg';
import { setNewProjectModalOpen, setProjectCandidate } from '../../redux/slices/projectSlice';
import { ProjectType } from '../../api/projects';
import { tableLoadModeSelector } from '../../redux/ducks/userSettings';
import { TABLE_LOAD_DATA_MODE } from '../../constants/settings';

const ProjectsTable = ({ handleCellClick }) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState<any>();
  const { data, isFetching, refetch } = useGetProjectsQuery(params, { skip: params === undefined });
  const { data: clientProject } = useGetClientProjectsQuery({ project_request: true });
  const { pageCount, pageIndex, pageSize } = useSelector((s: RootState) => s.pagination);
  const searchToken = useSelector((s: RootState) => s.filters.searchToken);
  const selectedFranchise = useSelector((s: RootState) => s.userAuth.info?.selectedFranchise);
  const tableLoadDataMode = useSelector(tableLoadModeSelector);
  const { data: voca } = useGetVocabularyQuery(6);
  const [rows, setRows] = useState([]);
  const [pendingPrj, setPendingPrj] = useState([]);

  useEffect(() => {
    dispatch(setProjectCandidate(null));
  }, []);
  useEffect(() => {
    const refresh = async () => {
      await refetch();
    };
    if (clientProject && !searchToken) {
      setPendingPrj(clientProject.results);
      refresh();
    } else if (clientProject) {
      const _filtered = clientProject.results.filter(cp => cp.address.toLowerCase().indexOf(searchToken.toLowerCase()) > -1);
      setPendingPrj(_filtered);
    }
  }, [clientProject, searchToken]);
  useEffect(() => {
    let _params: any = { limit: pageSize };
    if (searchToken && pageIndex > 0) {
      _params.offset = pageIndex * pageSize;
      _params.search = searchToken;
    } else {
      _params = searchToken
        ? { ..._params, search: searchToken }
        : {
            ..._params,
            offset: pageIndex * pageSize,
          };
    }

    if (tableLoadDataMode === TABLE_LOAD_DATA_MODE.ACTIVE) {
      _params.is_deleted = false;
    } else if (tableLoadDataMode === TABLE_LOAD_DATA_MODE.INACTIVE) {
      _params.is_deleted = true;
    }

    // Scope the request to the selected branch. The backend reads franchise_id from the
    // Django session, which only a request carrying ?franchise_id= updates - sending it
    // here makes the list correct whatever order the page's requests happen to fire in,
    // and keeps the RTK Query cache key distinct per branch.
    if (selectedFranchise) {
      _params.franchise_id = selectedFranchise;
    }

    setParams(_params);
  }, [pageSize, pageIndex, pageCount, searchToken, tableLoadDataMode, selectedFranchise]);

  useEffect(() => {
    dispatch(setPageIndex(0));
  }, [tableLoadDataMode]);

  useEffect(() => {
    if (data && data.count > -1) {
      dispatch(setRowsCount(data.count));
      setRows(data.results);
    }
  }, [data]);

  useEffect(() => {
    dispatch(setPageIndex(0));
  }, [searchToken]);

  const getStatus = (id: number) => {
    if (!voca) {
      return '';
    }

    const item = voca.results.find(v => v.id === id);
    if (item) {
      return item.title;
    }

    return '';
  };

  const openAddNewProjectPopup = (prjCadidate: ProjectType) => {
    dispatch(setNewProjectModalOpen(true));
    dispatch(setProjectCandidate(prjCadidate));
  };
  return (
    <div>
      {isFetching ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <TableCard>
          <HeaderRow>
            <HeaderCell>Project Name</HeaderCell>
            <HeaderCell>Permit Name</HeaderCell>
            <HeaderCell>Permit Number</HeaderCell>
            <HeaderCell>Permit Status</HeaderCell>
            <HeaderCell>Address</HeaderCell>
            <HeaderCell>Jurisdiction</HeaderCell>
            <HeaderCell>Project Manager</HeaderCell>
          </HeaderRow>
          <Rows>
            {pendingPrj.map((l, ndx) => {
              return (
                <RowBase key={ndx} newProject>
                  <TableCell>{l.name}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>{l.address}</TableCell>
                  <TableCell />
                  <TableCell full>
                    <LastCell>
                      <Manager>{l.projectManagerName}</Manager>
                      <IconWrapper onClick={() => openAddNewProjectPopup(l)}>
                        <img title="Create new project" src={newProject} />
                      </IconWrapper>
                    </LastCell>
                  </TableCell>
                </RowBase>
              );
            })}

            {rows.map((row, index) => {
              return (
                <RowBase key={index} zebra={index % 2 === 1}>
                  <TableCell>
                    <Underlined onClick={e => handleCellClick(row, 'name')}>{row.name}</Underlined>
                  </TableCell>
                  <PermitsCell>
                    <PermitsGrid>
                      {row.permits.map((permit, index) => {
                        const last = index === row.permits.length - 1;
                        return (
                          <React.Fragment key={index}>
                            <PermitRowCell last={last}>
                              <Underlined
                                onClick={e =>
                                  handleCellClick({ ...row, permitItemId: permit.id, projectId: row.id }, 'permit name')
                                }
                              >
                                <PermitName title={permit.name}>{permit.name}</PermitName>
                              </Underlined>
                            </PermitRowCell>
                            <PermitRowCell last={last}>{permit.number || <VoidPermitNumber>N/A</VoidPermitNumber>}</PermitRowCell>
                            <PermitRowCell last={last}>{getStatus(permit.status)}</PermitRowCell>
                          </React.Fragment>
                        );
                      })}
                    </PermitsGrid>
                  </PermitsCell>
                  <TableCell>{row.address}</TableCell>
                  <TableCell>{row.jurisdictionName}</TableCell>
                  <TableCell>
                    <Manager>{row.managerName}</Manager>
                  </TableCell>
                </RowBase>
              );
            })}
          </Rows>
        </TableCard>
      )}

      <Pagination>
        <PaginationMobile showEverywhere />
      </Pagination>
    </div>
  );
};

export default ProjectsTable;

const VoidPermitNumber = styled.span`
  visibility: hidden;
`;

const IconWrapper = styled.div`
  zoom: 0.05;
  cursor: pointer;
  flex-shrink: 0;
`;

const Manager = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LastCell = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;
`;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const TableCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow-xs);
`;

const GridBase = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  align-items: stretch;
`;

const HeaderRow = styled(GridBase)`
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border);
`;

const RowBase = styled(GridBase)<{ newProject?: boolean; zebra?: boolean }>`
  position: relative;
  border-bottom: 1px solid var(--border);
  background-color: ${({ newProject, zebra }) =>
    newProject
      ? 'var(--success-50)'
      : zebra
        ? 'var(--table-row-alt)'
        : 'var(--table-row-base)'};
  border-left: ${({ newProject }) => (newProject ? '3px solid var(--success-500)' : '3px solid transparent')};
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: ${({ newProject }) =>
      newProject ? 'var(--success-100, #DCEDC8)' : 'var(--table-row-hover)'};
  }
`;

const Rows = styled.div`
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PermitName = styled.div`
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.3;
`;

const PermitsCell = styled.div`
  grid-column: span 3;
  min-width: 0;
  padding: 14px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PermitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
`;

const PermitRowCell = styled.div<{ last?: boolean }>`
  min-width: 0;
  overflow: hidden;
  word-break: break-word;
  color: var(--mainText);
  font-size: 0.875rem;
  font-family: ${FONT_STACK};
  display: flex;
  align-items: center;
  padding: ${({ last }) => (last ? '0 16px' : '0 16px 8px')};
  margin-bottom: ${({ last }) => (last ? '0' : '8px')};
  border-bottom: ${({ last }) => (last ? 'none' : '1px solid var(--border)')};
`;

const Underlined = styled.div`
  color: var(--primary-600);
  cursor: pointer;
  font-weight: 500;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }
`;

const HeaderCell = styled.div`
  padding: 14px 16px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  font-family: ${FONT_STACK};
  display: flex;
  align-items: center;
`;

const TableCell = styled.div<{ full?: boolean }>`
  margin: 0;
  text-align: left;
  padding: 14px 16px;
  max-width: ${({ full }) => (full ? '100%' : '220px')};
  min-width: 0;
  overflow: hidden;
  word-break: break-word;
  color: var(--mainText);
  font-size: 0.875rem;
  font-family: ${FONT_STACK};
  display: flex;
  align-items: center;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center; ;
`;
